import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import PricingGrid from '@/components/PricingGrid';
import { Button } from '@/components/ui/button';
import { ScanEye, ArrowRight, ShieldCheck, Sparkles, CreditCard, Gift, HelpCircle, Bell, Mail } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const FAQ = [
  {
    q: 'Is there a free trial?',
    a: 'Yes — every paid plan (Try, Starter, Growth, and Enterprise) starts with a 7-day free trial. You add a payment method to start, get full access to that plan from day one, and pay nothing if you cancel before the 7 days are up. After the trial, your plan continues at its normal monthly price unless cancelled.',
  },
  {
    q: 'Which AI providers does GEOrush track?',
    a: 'Today we run scans across ChatGPT (OpenAI), Claude (Anthropic), and Gemini (Google) via our official API integrations. Perplexity, Microsoft Copilot, and Google AI Overview are on our roadmap — Perplexity can already be pre-connected in Settings with your own API key.',
  },
  {
    q: 'What counts as a "scan"?',
    a: 'A scan is one run of all your project prompts across all enabled AI providers. If you have 10 prompts and 3 providers enabled, that’s one scan (which internally makes 30 AI calls). We only count fully completed scans against your monthly quota.',
  },
  {
    q: 'Do I need my own OpenAI, Anthropic, or Google API key?',
    a: 'No. GEOrush includes AI usage in every paid plan. You never need to bring your own keys for ChatGPT, Claude, or Gemini scans.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. You can cancel from Settings at any time. Your plan stays active until the end of your current billing period, then downgrades to Free — no lost data.',
  },
  {
    q: 'What happens to my data if I downgrade?',
    a: 'All your projects, prompts, scans, and analytics stay intact. If a project or prompt count exceeds your new plan’s limits, they become read-only until you upgrade or archive some.',
  },
  {
    q: 'Do you offer annual pricing?',
    a: 'Not yet on self-serve. Growth/Enterprise customers who need annual invoicing can email sales@llmsplus.com for a quote.',
  },
  {
    q: 'Is my brand data private?',
    a: 'Yes. Your prompts and scan responses are stored in your isolated account. We never share your data with other customers or use it to train models.',
  },
];

export default function PricingPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/billing/plans');
        setPlans(r.data.plans || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <header className="sticky top-0 z-30 border-b bg-background/85 backdrop-blur">
        <div className="mx-auto flex max-w-[1240px] items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]"><ScanEye className="h-5 w-5" /></div>
            <div>
              <div className="font-display text-base font-semibold">GEOrush</div>
              <div className="text-[11px] text-muted-foreground -mt-0.5">AEO Visibility</div>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-6 text-sm">
            <Link to="/" className="text-muted-foreground hover:text-foreground">Home</Link>
            <Link to="/pricing" className="text-foreground font-medium">Pricing</Link>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/login')} data-testid="pricing-login-button">Sign in</Button>
            <Button size="sm" onClick={() => navigate('/register')} data-testid="pricing-signup-button">Get started</Button>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-mist pointer-events-none" />
        <div className="relative mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8 pt-16 pb-10 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))]" /> Pricing
          </div>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]">
            Simple, transparent pricing.
          </h1>
          <p className="mt-4 max-w-2xl mx-auto text-base md:text-lg text-muted-foreground">
            Pick a plan that fits. Every paid plan starts with a <span className="font-medium text-foreground">7-day free trial</span> — no charge for 7 days, cancel anytime.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">{[0, 1, 2, 3].map((i) => <div key={i} className="h-96 rounded-[var(--radius)] bg-secondary animate-pulse" />)}</div>
        ) : (
          <PricingGrid plans={plans} />
        )}
      </section>

      {/* Comparison */}
      <section className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="font-display text-2xl font-semibold tracking-tight text-center">Compare features</h2>
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b">
                <th className="py-3 pr-3 font-medium">Feature</th>
                {plans.map((p) => (
                  <th key={p.key} className="py-3 px-3 text-center font-display font-semibold">{p.name}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              <ComparisonRow label="Brand projects" values={plans.map((p) => fmtNum(p.limits?.projects))} />
              <ComparisonRow label="Prompts per project" values={plans.map((p) => fmtNum(p.limits?.prompts_per_project))} />
              <ComparisonRow label="Scans per month" values={plans.map((p) => fmtNum(p.limits?.scans_per_month))} />
              <ComparisonRow label="AI providers" values={plans.map((p) => (p.limits?.providers || []).length + '')} />
              <ComparisonRow label="AI-generated prompts" values={plans.map((p) => p.key === 'try' ? '—' : '✓')} />
              <ComparisonRow label="Competitor share of voice" values={plans.map((p) => (p.key === 'growth' || p.key === 'enterprise') ? '✓' : '—')} />
              <ComparisonRow label="Weekly reports (roadmap)" values={plans.map((p) => (p.key === 'growth' || p.key === 'enterprise') ? '✓' : '—')} />
              <ComparisonRow label="Support" values={plans.map((p) => p.key === 'try' ? 'Community' : p.key === 'starter' ? 'Email' : 'Priority email')} />
            </tbody>
          </table>
        </div>
      </section>

      {/* Trust / assurances */}
      <section className="bg-secondary/40 border-y">
        <div className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          <TrustCard icon={Gift} title="7-day free trial" body="Every paid plan starts free for 7 days. Cancel before it ends and pay nothing." />
          <TrustCard icon={ShieldCheck} title="Secure billing" body="Payments processed by Dodo Payments. We never store your card details." />
          <TrustCard icon={CreditCard} title="Cancel anytime" body="No lock-in. Cancel from Settings and stay active until period end." />
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="mx-auto max-w-[900px] px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
            <HelpCircle className="h-3.5 w-3.5" /> Questions
          </div>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-tight">Frequently asked questions</h2>
        </div>
        <div className="mt-8">
          <Accordion type="single" collapsible className="space-y-2">
            {FAQ.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="rounded-lg border bg-card px-4" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-medium py-4">{f.q}</AccordionTrigger>
                <AccordionContent className="pb-4 text-sm text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">
        <div className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8 py-14 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Ready to track your AI visibility?</h3>
            <p className="mt-2 text-sm opacity-80">Cancel anytime — no lock\'in.</p>
          </div>
          <Button size="lg" variant="secondary" onClick={() => navigate('/register')} data-testid="pricing-cta-bottom">
            Get started <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <footer className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8 py-8 text-xs text-muted-foreground flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div>© {new Date().getFullYear()} GEOrush — AEO Visibility & AI Citation Tracking</div>
        <div className="flex items-center gap-3 flex-wrap">
          <a
            href="mailto:support.georush@gmail.com"
            className="inline-flex items-center gap-1.5 hover:text-[hsl(var(--accent))] transition-colors"
            data-testid="footer-support-email"
          >
            <Mail className="h-3.5 w-3.5" /> support.georush@gmail.com
          </a>
          <span className="hidden md:inline text-border">·</span>
          <Bell className="h-3.5 w-3.5" /> Payment powered by Dodo Payments
        </div>
      </footer>
    </div>
  );
}

function TrustCard({ icon: Icon, title, body }) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[hsl(var(--accent)/0.12)] text-[hsl(var(--accent))]"><Icon className="h-5 w-5" /></div>
      <div>
        <div className="font-display font-semibold text-base">{title}</div>
        <div className="text-sm text-muted-foreground mt-0.5">{body}</div>
      </div>
    </div>
  );
}

function ComparisonRow({ label, values }) {
  return (
    <tr>
      <td className="py-3 pr-3 text-muted-foreground">{label}</td>
      {values.map((v, i) => (
        <td key={i} className="py-3 px-3 text-center font-medium">{v}</td>
      ))}
    </tr>
  );
}

function fmtNum(n) {
  if (n === null || n === undefined) return 'Unlimited';
  return String(n);
}
